import React, { useState, useRef, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faQrcode, 
    faSearch, 
    faCamera, 
    faCheckCircle, 
    faTimesCircle,
    faExclamationTriangle,
    faUser,
    faCalendarAlt,
    faBuilding,
    faClock
} from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import { getApiEndpoint } from '../../../config/api';

import jsQR from 'jsqr';
import './BuildingCheckIn.css';

const authHeaders = () => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
};

export default function BuildingCheckIn({ propertyId: propPropertyId }) {
    const [activeTab, setActiveTab] = useState('qr'); // 'qr' or 'search'
    const [searchId, setSearchId] = useState('');
    const [isScanning, setIsScanning] = useState(false);
    const [selectedPerson, setSelectedPerson] = useState(null);
    const [checkInHistory, setCheckInHistory] = useState([]);
    const [loading, setLoading] = useState(false);
    const [scanningDebug, setScanningDebug] = useState('Ready to scan...');
    const [properties, setProperties] = useState([]);
    const [selectedPropertyId, setSelectedPropertyId] = useState(propPropertyId || '');
    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    const streamRef = useRef(null);

    // Resolve effective property id: prop from parent or selected
    const propertyId = propPropertyId || selectedPropertyId || (properties[0] && properties[0].id);
    const effectivePropertyId = propertyId ? String(propertyId) : null;

    useEffect(() => {
        if (propPropertyId) return;
        const token = localStorage.getItem('token');
        fetch(getApiEndpoint('/api/properties'), { headers: { Authorization: `Bearer ${token}` } })
            .then(res => res.ok ? res.json() : [])
            .then(data => {
                const list = Array.isArray(data) ? data : (data.properties || data.list || []);
                setProperties(list);
                if (list.length && !selectedPropertyId) setSelectedPropertyId(String(list[0].id));
            })
            .catch(() => setProperties([]));
    }, [propPropertyId]);


    // Mock data for demonstration - replace with real API calls
    const mockPersonData = {
        'STAFF001': {
            id: 'STAFF001',
            name: 'John Smith',
            role: 'Safety Officer',
            type: 'staff',
            department: 'Facilities',
            inductionStatus: 'Complete',
            inductionDate: '2024-01-15',
            expiryDate: '2025-01-15',
            photo: null,
            certifications: ['First Aid', 'Fire Safety', 'Working at Height']
        },
        'CONT002': {
            id: 'CONT002',
            name: 'Sarah Jones',
            role: 'Contractor',
            type: 'contractor',
            company: 'ABC Construction',
            inductionStatus: 'Expired',
            inductionDate: '2023-06-10',
            expiryDate: '2024-06-10',
            photo: null,
            certifications: ['Basic Safety']
        },
        'STAFF003': {
            id: 'STAFF003',
            name: 'Mike Wilson',
            role: 'Maintenance',
            type: 'staff',
            department: 'Engineering',
            inductionStatus: 'Incomplete',
            inductionDate: null,
            expiryDate: null,
            photo: null,
            certifications: []
        },
        'CONT001': {
            id: 'CONT001',
            name: 'Alex Rodriguez',
            role: 'Contractor',
            type: 'contractor',
            company: 'Elite Safety Solutions',
            inductionStatus: 'Complete',
            inductionDate: '2024-02-01',
            expiryDate: '2025-02-01',
            photo: null,
            certifications: ['Working at Height', 'Rope Access', 'First Aid']
        },
        'CONT003': {
            id: 'CONT003',
            name: 'Emma Thompson',
            role: 'Contractor',
            type: 'contractor',
            company: 'Thompson Building Services',
            inductionStatus: 'Complete',
            inductionDate: '2024-01-20',
            expiryDate: '2025-01-20',
            photo: null,
            certifications: ['Building Maintenance', 'Electrical Safety']
        }
    };

    const startQRScanning = async () => {
        try {
            setIsScanning(true);
            console.log('Starting QR scanner...');
            
            // Enhanced camera constraints for better scanning
            const constraints = {
                video: {
                    facingMode: 'environment', // Use back camera if available
                    width: { ideal: 1280 },
                    height: { ideal: 720 },
                    focusMode: 'continuous',
                    exposureMode: 'continuous',
                    whiteBalanceMode: 'continuous'
                }
            };
            
            const stream = await navigator.mediaDevices.getUserMedia(constraints);
            
            if (videoRef.current) {
                videoRef.current.srcObject = stream;
                streamRef.current = stream;
                
                // Wait for video to be ready and start scanning
                videoRef.current.onloadedmetadata = () => {
                    console.log('Video metadata loaded, starting playback...');
                    videoRef.current.play();
                    
                    // Start scanning after video is fully loaded
                    setTimeout(() => {
                        if (isScanning) {
                            console.log('Starting QR code detection...');
                            scanQRCode();
                        }
                    }, 1000); // Longer delay for better stability
                };
            }
        } catch (error) {
            console.error('Error accessing camera:', error);
            // Try with basic constraints if enhanced ones fail
            try {
                const basicStream = await navigator.mediaDevices.getUserMedia({ 
                    video: true 
                });
                
                if (videoRef.current) {
                    videoRef.current.srcObject = basicStream;
                    streamRef.current = basicStream;
                    videoRef.current.play();
                    setTimeout(() => {
                        if (isScanning) {
                            scanQRCode();
                        }
                    }, 1000);
                }
                toast.warning('Using basic camera settings. QR scanning may be limited.');
            } catch (basicError) {
                toast.error('Could not access camera. Please check permissions.');
                setIsScanning(false);
            }
        }
    };

    const stopQRScanning = () => {
        if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => track.stop());
            streamRef.current = null;
        }
        setIsScanning(false);
        if (videoRef.current) {
            videoRef.current.srcObject = null;
        }
    };

    const scanQRCode = () => {
        if (!isScanning || !videoRef.current || !canvasRef.current) return;

        const video = videoRef.current;
        const canvas = canvasRef.current;
        const context = canvas.getContext('2d');

        if (video.readyState === video.HAVE_ENOUGH_DATA) {
            setScanningDebug('Scanning for QR codes...');
            canvas.height = video.videoHeight;
            canvas.width = video.videoWidth;
            context.drawImage(video, 0, 0, canvas.width, canvas.height);
            
            const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
            const code = jsQR(imageData.data, imageData.width, imageData.height, {
                inversionAttempts: "attemptBoth", // Try both normal and inverted
            });

            if (code) {
                console.log('QR Code detected:', code.data);
                setScanningDebug(`QR Code found: ${code.data.substring(0, 30)}...`);
                
                // Handle CloudSafe QR codes (new format)
                if (code.data.startsWith('CS-CONTRACTOR-')) {
                    const contractorId = code.data.replace('CS-CONTRACTOR-', '');
                    console.log('Contractor ID extracted:', contractorId);
                    setScanningDebug(`Processing contractor: ${contractorId}`);
                    handlePersonLookup(contractorId, { type: 'contractor', role: 'contractor' });
                    stopQRScanning();
                    return;
                }
                
                if (code.data.startsWith('CS-STAFF-')) {
                    const staffId = code.data.replace('CS-STAFF-', '');
                    console.log('Staff ID extracted:', staffId);
                    setScanningDebug(`Processing staff: ${staffId}`);
                    handlePersonLookup(staffId, { type: 'staff', role: 'staff' });
                    stopQRScanning();
                    return;
                }
                
                // Handle old JSON format (backward compatibility)
                try {
                    const qrData = JSON.parse(code.data);
                    if (qrData.cloudSafeId) {
                        setScanningDebug(`Processing legacy QR: ${qrData.cloudSafeId}`);
                        handlePersonLookup(qrData.cloudSafeId, qrData);
                        stopQRScanning();
                        return;
                    }
                } catch (e) {
                    // Not JSON, treat as simple ID string
                    console.log('Simple ID detected:', code.data);
                    setScanningDebug(`Processing simple ID: ${code.data}`);
                    handlePersonLookup(code.data);
                    stopQRScanning();
                    return;
                }
            } else {
                setScanningDebug('Scanning... (no QR code detected)');
            }
        } else {
            setScanningDebug('Waiting for camera to be ready...');
        }

        // Continue scanning
        if (isScanning) {
            requestAnimationFrame(scanQRCode);
        }
    };

    const handlePersonLookup = async (personId, qrData = null) => {
        setLoading(true);
        try {
            let personData = null;

            // If we have QR data, we can extract some info directly
            if (qrData) {
                console.log('Processing QR data:', qrData);
                
                // Determine if it's a contractor or staff member
                const isContractor = qrData.type === 'contractor' || qrData.role === 'contractor';
                
                // Try to fetch from appropriate API endpoint
                try {
                    const endpoint = isContractor ? 
                        getApiEndpoint(`/api/users/contractor/${personId}`) : 
                        getApiEndpoint(`/api/users/staff/${personId}`);
                    
                    const response = await fetch(endpoint, { headers: authHeaders() });
                    if (response.ok) {
                        const apiData = await response.json();
                        personData = {
                            id: personId,
                            name: qrData.contractorName || qrData.staffName || `${apiData.first_name} ${apiData.last_name}`,
                            role: qrData.role || apiData.role,
                            type: isContractor ? 'contractor' : 'staff',
                            company: qrData.company || apiData.company,
                            department: apiData.department,
                            email: qrData.email || apiData.email,
                            inductionStatus: qrData.certification === 'Active' ? 'Complete' : 'Unknown',
                            inductionDate: qrData.issueDate,
                            expiryDate: qrData.validUntil,
                            photo: apiData.photo,
                            certifications: apiData.certifications || []
                        };
                    }
                } catch (apiError) {
                    console.warn('API call failed, using QR data:', apiError);
                    // Fallback to QR data if API fails
                    personData = {
                        id: personId,
                        name: qrData.contractorName || qrData.staffName,
                        role: qrData.role,
                        type: qrData.type || (qrData.role === 'contractor' ? 'contractor' : 'staff'),
                        company: qrData.company,
                        email: qrData.email,
                        inductionStatus: qrData.certification === 'Active' ? 'Complete' : 'Unknown',
                        inductionDate: qrData.issueDate,
                        expiryDate: qrData.validUntil,
                        photo: null,
                        certifications: []
                    };
                }
            } else {
                // Simple ID lookup - normalize the input
                const normalizedId = personId.trim().toUpperCase();
                console.log('Searching for person with ID:', normalizedId);
                
                // Try mock data first with direct lookup
                personData = mockPersonData[normalizedId];
                
                // If not found, try partial matching and email lookups
                if (!personData) {
                    // Check if it's an email format and try demo mappings
                    if (personId.includes('@')) {
                        if (personId.toLowerCase().includes('contractor') || personId.toLowerCase().includes('cont')) {
                            personData = mockPersonData['CONT001'];
                            console.log('Email detected as contractor, using CONT001');
                        } else if (personId.toLowerCase().includes('staff')) {
                            personData = mockPersonData['STAFF001'];
                            console.log('Email detected as staff, using STAFF001');
                        }
                    } else {
                        // Try partial matching for IDs (e.g., "CONT1" -> "CONT001")
                        const mockKeys = Object.keys(mockPersonData);
                        const matchedKey = mockKeys.find(key => 
                            key.startsWith(normalizedId) || 
                            normalizedId.startsWith(key.substring(0, 4)) // Match first 4 chars
                        );
                        
                        if (matchedKey) {
                            personData = mockPersonData[matchedKey];
                            console.log(`Partial match found: ${normalizedId} -> ${matchedKey}`);
                        }
                    }
                }
                
                // If still not found, try API lookup
                if (!personData) {
                    console.log('No mock data found, trying API lookup...');
                    try {
                        // Try staff first
                        let response = await fetch(getApiEndpoint(`/api/users/staff/${personId}`), { headers: authHeaders() });
                        if (!response.ok) {
                            // If staff fails, try contractor
                            console.log('Staff lookup failed, trying contractor...');
                            response = await fetch(getApiEndpoint(`/api/users/contractor/${personId}`), { headers: authHeaders() });
                        }
                        
                        if (response.ok) {
                            const apiData = await response.json();
                            console.log('API data found:', apiData);
                            personData = {
                                id: personId,
                                name: `${apiData.first_name} ${apiData.last_name}`,
                                role: apiData.role,
                                type: apiData.role === 'contractor' ? 'contractor' : 'staff',
                                company: apiData.company,
                                department: apiData.department,
                                email: apiData.email,
                                inductionStatus: 'Complete', // Default for existing users
                                inductionDate: apiData.created_at?.split('T')[0],
                                expiryDate: null,
                                photo: apiData.photo,
                                certifications: apiData.certifications || []
                            };
                        } else {
                            console.log('API lookup failed for both staff and contractor');
                        }
                    } catch (apiError) {
                        console.warn('API lookup failed:', apiError);
                    }
                }
            }
            
            if (personData) {
                setSelectedPerson(personData);
                toast.success(`Found: ${personData.name} (${personData.type === 'contractor' ? 'Contractor' : 'Staff'})`);
            } else {
                toast.error('Person not found in system');
                setSelectedPerson(null);
            }
        } catch (error) {
            console.error('Error looking up person:', error);
            toast.error('Error looking up person');
        } finally {
            setLoading(false);
        }
    };

    const handleCheckIn = async () => {
        if (!selectedPerson) return;
        if (!effectivePropertyId) {
            toast.error('Please select a property first');
            return;
        }

        try {
            const checkInData = {
                personId: selectedPerson.id,
                name: selectedPerson.name,
                timestamp: new Date().toISOString(),
                inductionStatus: selectedPerson.inductionStatus,
                propertyId: effectivePropertyId,
                reason: 'Building access',
                personRole: selectedPerson.type === 'contractor' ? 'contractor' : 'staff',
                company: selectedPerson.company || null,
                certificationValid: selectedPerson.inductionStatus === 'Complete',
                inductionStatus: selectedPerson.inductionStatus === 'Complete' ? 'completed' : selectedPerson.inductionStatus === 'Expired' ? 'expired' : 'pending'
            };

            const token = localStorage.getItem('token');
            const response = await fetch(getApiEndpoint('/api/users/checkin'), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify(checkInData)
            });

            if (response.ok) {
                const result = await response.json();
                setCheckInHistory(prev => [{
                    personId: selectedPerson.id,
                    name: selectedPerson.name,
                    timestamp: checkInData.timestamp,
                    inductionStatus: selectedPerson.inductionStatus
                }, ...prev.slice(0, 9)]);
            } else {
                const err = await response.json().catch(() => ({}));
                toast.error(err.message || 'Failed to record check-in');
                return;
            }

            toast.success(`${selectedPerson.name} checked in successfully`);
            setSelectedPerson(null);
            setSearchId('');
        } catch (error) {
            console.error('Error checking in:', error);
            toast.error('Error during check-in');
        }
    };

    const clearSearch = () => {
        setSearchId('');
        setSelectedPerson(null);
    };

    const getStatusColor = (status) => {
        switch (status?.toLowerCase()) {
            case 'complete': return '#28a745';
            case 'expired': return '#dc3545';
            case 'incomplete': return '#ffc107';
            default: return '#6c757d';
        }
    };

    const getStatusIcon = (status) => {
        switch (status?.toLowerCase()) {
            case 'complete': return faCheckCircle;
            case 'expired': return faTimesCircle;
            case 'incomplete': return faExclamationTriangle;
            default: return faTimesCircle;
        }
    };

    useEffect(() => {
        return () => {
            stopQRScanning();
        };
    }, []);

    return (
        <div className="building-checkin">
            <div className="checkin-header">
                <h2>Building Check-In</h2>
                <p>Scan QR code or search by ID to check people into the building</p>
            </div>

            {!propPropertyId && properties.length > 1 && (
                <div className="checkin-property-select" style={{ marginBottom: '1rem' }}>
                    <label htmlFor="checkin-property">Property: </label>
                    <select
                        id="checkin-property"
                        value={selectedPropertyId}
                        onChange={(e) => setSelectedPropertyId(e.target.value)}
                        style={{ padding: '0.5rem', minWidth: '200px' }}
                    >
                        {properties.map((p) => (
                            <option key={p.id} value={String(p.id)}>{p.name}</option>
                        ))}
                    </select>
                </div>
            )}

            <div className="checkin-tabs">
                <button 
                    className={`tab ${activeTab === 'qr' ? 'active' : ''}`}
                    onClick={() => setActiveTab('qr')}
                >
                    <FontAwesomeIcon icon={faQrcode} /> QR Scanner
                </button>
                <button 
                    className={`tab ${activeTab === 'search' ? 'active' : ''}`}
                    onClick={() => setActiveTab('search')}
                >
                    <FontAwesomeIcon icon={faSearch} /> ID Search
                </button>
            </div>

            <div className="checkin-content">
                {activeTab === 'qr' && (
                    <div className="qr-scanner-section">
                        <div className="scanner-container">
                            {!isScanning ? (
                                <div className="scanner-placeholder">
                                    <FontAwesomeIcon icon={faCamera} size="3x" />
                                    <h3>QR Code Scanner</h3>
                                    <p>Click to start scanning QR codes</p>
                                    <button 
                                        className="start-scan-btn"
                                        onClick={startQRScanning}
                                    >
                                        <FontAwesomeIcon icon={faQrcode} /> Start Scanning
                                    </button>
                                </div>
                            ) : (
                                <div className="scanner-active">
                                    <video 
                                        ref={videoRef}
                                        className="scanner-video"
                                        autoPlay 
                                        playsInline 
                                        muted
                                    />
                                    <div className="scanner-overlay">
                                        <div className="scanner-frame"></div>
                                        <p>Position QR code within the frame</p>
                                        <div className="scanner-debug">
                                            {scanningDebug}
                                        </div>
                                        <button 
                                            className="stop-scan-btn"
                                            onClick={stopQRScanning}
                                        >
                                            Stop Scanning
                                        </button>
                                    </div>
                                </div>
                            )}
                            <canvas ref={canvasRef} style={{ display: 'none' }} />
                        </div>
                    </div>
                )}

                {activeTab === 'search' && (
                    <div className="id-search-section">
                        <div className="search-container">
                            <div className="search-input-group">
                                <FontAwesomeIcon icon={faSearch} className="search-icon" />
                                <input
                                    type="text"
                                    placeholder="Enter Staff ID, Contractor ID, or Email (e.g., STAFF001, CONT002, user@example.com)"
                                    value={searchId}
                                    onChange={(e) => setSearchId(e.target.value)}
                                    onKeyPress={(e) => e.key === 'Enter' && handlePersonLookup(searchId.trim())}
                                />
                                <button 
                                    className="search-btn"
                                    onClick={() => handlePersonLookup(searchId.trim())}
                                    disabled={!searchId.trim() || loading}
                                >
                                    {loading ? 'Searching...' : 'Search'}
                                </button>
                                {searchId && (
                                    <button 
                                        className="clear-btn"
                                        onClick={clearSearch}
                                        disabled={loading}
                                    >
                                        Clear
                                    </button>
                                )}
                            </div>
                            <div className="search-help">
                                <p><strong>Staff IDs:</strong> STAFF001, STAFF003</p>
                                <p><strong>Contractor IDs:</strong> CONT001, CONT002, CONT003</p>
                                <p><strong>Partial search:</strong> Try "CONT" or "STAFF"</p>
                                <p><strong>Email examples:</strong> contractor@test.com, staff@test.com</p>
                            </div>
                        </div>
                    </div>
                )}

                {selectedPerson && (
                    <div className="person-details">
                        <div className="person-card">
                            <div className="person-header">
                                <div className="person-photo">
                                    <FontAwesomeIcon icon={faUser} size="2x" />
                                </div>
                                <div className="person-info">
                                    <h3>{selectedPerson.name}</h3>
                                    <p className="person-id">ID: {selectedPerson.id}</p>
                                    <p className="person-role">
                                        {selectedPerson.role} {selectedPerson.type === 'contractor' && '(Contractor)'}
                                    </p>
                                    {selectedPerson.department && (
                                        <p className="person-dept">
                                            <FontAwesomeIcon icon={faBuilding} /> {selectedPerson.department}
                                        </p>
                                    )}
                                    {selectedPerson.company && (
                                        <p className="person-company">
                                            <FontAwesomeIcon icon={faBuilding} /> {selectedPerson.company}
                                        </p>
                                    )}
                                    {selectedPerson.email && (
                                        <p className="person-email">
                                            Email: {selectedPerson.email}
                                        </p>
                                    )}
                                </div>
                            </div>

                            <div className="induction-status">
                                <div className="status-header">
                                    <FontAwesomeIcon 
                                        icon={getStatusIcon(selectedPerson.inductionStatus)}
                                        style={{ color: getStatusColor(selectedPerson.inductionStatus) }}
                                    />
                                    <span 
                                        className="status-text"
                                        style={{ color: getStatusColor(selectedPerson.inductionStatus) }}
                                    >
                                        Induction {selectedPerson.inductionStatus}
                                    </span>
                                </div>
                                
                                {selectedPerson.inductionDate && (
                                    <div className="status-dates">
                                        <p>
                                            <FontAwesomeIcon icon={faCalendarAlt} />
                                            Completed: {new Date(selectedPerson.inductionDate).toLocaleDateString()}
                                        </p>
                                        {selectedPerson.expiryDate && (
                                            <p>
                                                <FontAwesomeIcon icon={faCalendarAlt} />
                                                Expires: {new Date(selectedPerson.expiryDate).toLocaleDateString()}
                                            </p>
                                        )}
                                    </div>
                                )}

                                {selectedPerson.certifications.length > 0 && (
                                    <div className="certifications">
                                        <h4>Certifications:</h4>
                                        <div className="cert-tags">
                                            {selectedPerson.certifications.map((cert, index) => (
                                                <span key={index} className="cert-tag">{cert}</span>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="checkin-actions">
                                {selectedPerson.inductionStatus === 'Complete' ? (
                                    <button 
                                        className="checkin-btn success"
                                        onClick={handleCheckIn}
                                    >
                                        <FontAwesomeIcon icon={faCheckCircle} />
                                        Check In to Building
                                    </button>
                                ) : (
                                    <div className="checkin-warning">
                                        <FontAwesomeIcon icon={faExclamationTriangle} />
                                        <span>
                                            {selectedPerson.inductionStatus === 'Expired' 
                                                ? 'Induction expired - renewal required before entry'
                                                : 'Induction incomplete - cannot enter building'
                                            }
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {checkInHistory.length > 0 && (
                    <div className="checkin-history">
                        <h3>Recent Check-ins</h3>
                        <div className="history-list">
                            {checkInHistory.map((entry, index) => (
                                <div key={index} className="history-item">
                                    <div className="history-info">
                                        <span className="history-name">{entry.name}</span>
                                        <span className="history-id">({entry.personId})</span>
                                    </div>
                                    <div className="history-time">
                                        <FontAwesomeIcon icon={faClock} />
                                        {new Date(entry.timestamp).toLocaleTimeString()}
                                    </div>
                                    <div 
                                        className="history-status"
                                        style={{ color: getStatusColor(entry.inductionStatus) }}
                                    >
                                        {entry.inductionStatus}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
} 